import {
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useShallow } from 'zustand/react/shallow'
import { v4 as uuidv4 } from 'uuid'
import { ChatBubbleOvalLeftEllipsisIcon } from '@heroicons/react/24/outline'
import queryString from 'query-string'

import { useDocumentState } from '@/state'
import { DocumentTemplate } from '../DocumentTemplate'
import { type FlattenedDocumentPage, type View } from '@/lib/api/client'
import { useDocumentPageContextQuery } from '../../queries'
import { useOperationPageTemplateAttributes } from './hooks/useOperationPageTemplateAttributes'
import { useListImages } from '@/services/queries/images'
import { AnnotationsContainer } from '../Annotations/AnnotationsContainer'
import { cleanAndFilterViews } from '@/utils/document_preview'
import { isEmptyOrSpaces } from '@/utils'
import { useStepBOM } from './hooks/useBOM'
import { EmptyDocumentIcon } from '@/components/icons/EmptyDocumentIcon'
import { Button } from '@/components/ui/button'
import { useLocation, useRoute, useSearch } from 'wouter'
import { useUpdateDocumentPage } from '@/services/queries/document_pages'
import { useListComments } from '@/services/queries/comments'
import { useDeleteTemplateAttribute } from '@/services/queries/template_attributes'
import { useListTemplateAttributes } from '@/services/queries/template_attributes'
import { AnnotationsMultiSelectContainer } from '@/pages/DocumentPage/features/templateAttributesMultiSelect/AnnotationsMultiSelectContainer'
import { useActiveSubpageFromVirtualizer } from './hooks/useActiveSubpageFromVirtualizer'
import { SubpageControls } from './SubpageControls'
import { isMultiSelectModifierPressed } from '@/pages/DocumentPage/features/templateAttributesMultiSelect/isMultiSelectModifierPressed'

const OPERATION_DOCUMENTS_OFFSET = 1000000
const EMPTY_STATE_BANNER_DISMISS_KEY = 'empty_state_banner_dismissed'
const PRINT_SUBPAGE_CONTENT_OFFSET_Y = -64

const shouldIgnoreRecentKonvaAnnotationClick = () => {
  const lastKonvaAnnotationPointerDownAt = (
    window as Window & {
      __q20LastKonvaAnnotationPointerDownAt?: number
    }
  ).__q20LastKonvaAnnotationPointerDownAt

  return (
    typeof lastKonvaAnnotationPointerDownAt === 'number' &&
    Date.now() - lastKonvaAnnotationPointerDownAt < 500
  )
}

export const OperationDocument = ({
  documentPage,
  views,
  versionName,
  createdAt,
  orderNumber,
  isReadOnly,
  isLatestDocumentVersion,
  fullScreen,
  hideCommentsButton,
  parentRef,
}: {
  documentPage: FlattenedDocumentPage
  views: View[]
  versionName: string
  createdAt: string
  orderNumber: number | string
  isReadOnly: boolean
  isLatestDocumentVersion?: boolean
  fullScreen?: boolean
  hideCommentsButton?: boolean
  parentRef?: boolean
}) => {
  const { data: docData } = useDocumentPageContextQuery()
  const search = useSearch()
  const [isPrintPage] = useRoute(/print$/)
  const queryParams = useMemo(
    () => queryString.parse(search) as { subpage_id?: string },
    [search],
  )
  const setSelectedElement = useDocumentState(
    (state) => state.setSelectedElement,
  )
  const currentPageInView = useDocumentState(
    useShallow((state) => state.currentPageInView),
  )
  const isInView = useMemo(
    () => currentPageInView === documentPage.id,
    [currentPageInView, documentPage.id],
  )
  const hasSelectedElement = useDocumentState(
    (state) => state.selectedElement !== null,
  )
  const partsInOperation = useStepBOM({
    documentPageId: documentPage.id,
  })
  const { data: images } = useListImages({
    documentVersionId: documentPage.document_version,
  })

  const {
    notesTemplates,
    labelTemplates,
    imageTemplates,
    viewTemplates,
    genericTables,
    manualPartsTables,
    manualToolsTables,
  } = useOperationPageTemplateAttributes({ documentPageId: documentPage.id })
  const { data: allDocumentTemplates } = useListTemplateAttributes({
    documentPageId: documentPage.id,
  })
  const [isEmptyStateBannerDismissed, setIsEmptyStateBannerDismissed] =
    useState(false)

  const isDocumentThemeEnabled = useMemo(() => {
    return Boolean(documentPage.template_values.created_using_document_theme)
  }, [documentPage.template_values.created_using_document_theme])

  const { mutateAsync: updateOperation } = useUpdateDocumentPage()
  const { mutateAsync: deleteTemplate } = useDeleteTemplateAttribute()

  const { data: documentPageComments } = useListComments({
    documentPageId: documentPage.id,
  })
  const unresolvedComments = documentPageComments?.filter(
    (comment) => comment.comment_status === 'unresolved',
  )
  const setCommentsEnabled = useDocumentState(
    (state) => state.setCommentsEnabled,
  )
  const isAnyDialogOpen = useCallback(() => {
    /**
     * Invariant: global page click handlers are paused while modal dialogs are open.
     */
    return Boolean(document.querySelector('[role="dialog"]'))
  }, [])

  const unselectElement = useCallback(
    (event: MouseEvent) => {
      if (isAnyDialogOpen()) return
      if (
        isMultiSelectModifierPressed(
          event,
          useDocumentState.getState().isSelectKeyPressed,
        )
      ) {
        return
      }

      const target = event.target
      if (!(target instanceof HTMLElement)) return

      if (
        target.closest('[role="menu"]') ||
        target.closest('[data-radix-context-menu-content]') ||
        target.closest('[data-radix-popper-content-wrapper]') ||
        target.closest('[data-konva-annotation-layer]')
      ) {
        return
      }
      if (shouldIgnoreRecentKonvaAnnotationClick()) {
        return
      }
      setSelectedElement(null)
    },
    [isAnyDialogOpen, setSelectedElement],
  )

  useEffect(() => {
    setIsEmptyStateBannerDismissed(
      !!localStorage.getItem(EMPTY_STATE_BANNER_DISMISS_KEY),
    )
  }, [])

  useEffect(() => {
    if (hasSelectedElement) {
      document.addEventListener('click', unselectElement)
    } else {
      document.removeEventListener('click', unselectElement)
    }

    return () => {
      document.removeEventListener('click', unselectElement)
    }
  }, [unselectElement, hasSelectedElement])

  const imageIds = useMemo(
    () =>
      imageTemplates
        .map((imageTemplate) => imageTemplate.template_values.imageId)
        .filter((id): id is string => Boolean(id)),
    [imageTemplates],
  )
  const imageIdSet = useMemo(() => new Set(imageIds), [imageIds])
  const documentImages = useMemo(
    () =>
      (images || []).filter(
        (image) => Boolean(image.id) && imageIdSet.has(image.id as string),
      ),
    [imageIdSet, images],
  )

  const relativeImages = cleanAndFilterViews({
    viewData: views as View[],
    viewsTemplates: viewTemplates.map((template) => template.template_values),
    documentPageId: documentPage.id as string,
  })

  const absoluteImages = viewTemplates
    .filter((template) => template.template_values.imagePosition === 'absolute')
    .map((template) => {
      const viewId = template.template_values.viewId
      const view = views.find((v) => v.id === viewId)
      return view
    })
    .filter((view) => view !== undefined)
  const nonEmptyRelativeImagesCount = useMemo(
    () => relativeImages.filter((img: any) => img.type !== 'empty').length,
    [relativeImages],
  )

  const documentOrder = useMemo(() => {
    const [whole, ...rest] = orderNumber.toString().split('.')

    if (!rest.length) return Number(whole)

    return Number(
      `${whole}.${rest.reduce((accum, num) => accum + Number(num), 0)}`,
    )
  }, [orderNumber])

  const shouldRenderEmptyDocumentBanner = useMemo(
    () =>
      !isEmptyStateBannerDismissed &&
      Number(orderNumber) === 1 &&
      partsInOperation.length === 0 &&
      manualPartsTables.length === 0 &&
      manualToolsTables.length === 0 &&
      genericTables.length === 0 &&
      absoluteImages.length === 0 &&
      nonEmptyRelativeImagesCount === 0 &&
      notesTemplates.length === 0 &&
      labelTemplates.length === 0 &&
      documentImages.length === 0,
    [
      partsInOperation,
      manualPartsTables,
      manualToolsTables,
      genericTables,
      absoluteImages,
      nonEmptyRelativeImagesCount,
      notesTemplates,
      labelTemplates,
      documentImages,
      isEmptyStateBannerDismissed,
      orderNumber,
    ],
  )

  const shouldRenderNoViewsBanner = useMemo(
    () =>
      !isDocumentThemeEnabled &&
      !isEmptyStateBannerDismissed &&
      Number(orderNumber) === 1 &&
      !shouldRenderEmptyDocumentBanner &&
      absoluteImages.length === 0 &&
      viewTemplates.length === 0 &&
      nonEmptyRelativeImagesCount === 0 &&
      documentImages.length === 0,
    [
      isDocumentThemeEnabled,
      nonEmptyRelativeImagesCount,
      absoluteImages,
      documentImages,
      viewTemplates,
      shouldRenderEmptyDocumentBanner,
      isEmptyStateBannerDismissed,
      orderNumber,
    ],
  )

  const onDismissEmptyStateBanner = useCallback(() => {
    setIsEmptyStateBannerDismissed(true)
    localStorage.setItem(EMPTY_STATE_BANNER_DISMISS_KEY, 'true')
  }, [])

  const subPages: string[] = useMemo(
    () => documentPage?.template_values?.subPages || [documentPage.id],
    [documentPage?.template_values?.subPages, documentPage.id],
  )

  const hiddenSubPageIds = useMemo(
    () =>
      new Set<string>(
        (documentPage.template_values?.hiddenSubPages as
          | string[]
          | undefined) ?? [],
      ),
    [documentPage.template_values?.hiddenSubPages],
  )

  // When there are no real subpages the document page itself is the only
  // "subpage" (fallback = [documentPage.id]). In that case the eye icon in the
  // floating toolbar should toggle the whole page hidden (recoverable from the
  // TOC), not add documentPage.id to hiddenSubPages (irrecoverable because the
  // SectionsList never renders for single-subpage operations).
  const hasRealSubPages = Boolean(
    (documentPage.template_values?.subPages as string[] | undefined)?.length,
  )

  const effectiveSubPages = useMemo(
    () => subPages.filter((id) => !hiddenSubPageIds.has(id)),
    [subPages, hiddenSubPageIds],
  )

  // Patched documentPage whose template_values.subPages reflects only visible
  // subpages. Passed to AnnotationsContainer (and down to DraggableAnnotation,
  // Line, AnnotationsContainer itself) so every getAbsoluteYFromSubPage call
  // uses the correct filtered index. Must NOT be used in mutation callbacks
  // (addSubpage, removeSubpage, SubpageControls) which need the full list.
  const patchedDocumentPage = useMemo(
    () =>
      ({
        ...documentPage,
        template_values: {
          ...documentPage.template_values,
          subPages: effectiveSubPages,
        },
      }) as unknown as FlattenedDocumentPage,
    [documentPage, effectiveSubPages],
  )

  useActiveSubpageFromVirtualizer({
    operationPageId: documentPage.id as string,
    subPageIds: effectiveSubPages,
    debounceMs: 50,
  })

  useEffect(() => {
    const subPageIdFromUrl = queryParams.subpage_id

    if (subPageIdFromUrl && Array.isArray(subPages)) {
      const index = subPages.findIndex((p) => p === subPageIdFromUrl)

      if (index >= 0) {
        const el = document.getElementById(`sub-page-index-${index - 1}`)

        if (el) {
          setTimeout(() => {
            el.scrollIntoView({ behavior: 'instant', block: 'start' })
          }, 100)
        }
      }
    }
  }, [queryParams.subpage_id, subPages])

  const addSubpage = useCallback(
    (newSubPageId: string, afterSubPageId: string) => {
      const insertIdx = subPages.indexOf(afterSubPageId)
      const newSubPages =
        insertIdx === -1
          ? [...subPages, newSubPageId]
          : [
              ...subPages.slice(0, insertIdx + 1),
              newSubPageId,
              ...subPages.slice(insertIdx + 1),
            ]
      void updateOperation({
        documentPageId: documentPage.id as string,
        values: {
          name: documentPage.name,
          template_values: {
            ...documentPage.template_values,
            subPages: newSubPages,
          },
          parent_id: documentPage.parent as string,
        },
      })
    },
    [documentPage, subPages, updateOperation],
  )

  const removeSubpage = useCallback(
    async (subPageId) => {
      const existingHidden =
        (documentPage.template_values?.hiddenSubPages as
          | string[]
          | undefined) ?? []
      void updateOperation({
        documentPageId: documentPage.id as string,
        values: {
          name: documentPage.name,
          template_values: {
            ...documentPage.template_values,
            subPages: subPages.filter((id) => id !== subPageId),
            // T023: also clean removed subpage from hiddenSubPages
            hiddenSubPages: existingHidden.filter((id) => id !== subPageId),
          },
          parent_id: documentPage.parent as string,
        },
      })
      const subpageTemplates =
        allDocumentTemplates?.filter(
          (template) => template.template_values.subPageId === subPageId,
        ) || []

      await Promise.all(
        subpageTemplates?.map((template) =>
          deleteTemplate({
            templateAttributeId: template.id as string,
            documentPageId: documentPage.id as string,
          }),
        ),
      )
    },
    [
      documentPage,
      subPages,
      allDocumentTemplates,
      updateOperation,
      deleteTemplate,
    ],
  )

  if (
    !docData ||
    !docData.project ||
    !docData.version ||
    !docData.documentPages
  ) {
    return null
  }

  // Hide entire document when all subpages are hidden
  if (effectiveSubPages.length === 0) {
    return null
  }

  const title = isEmptyOrSpaces(documentPage.name)
    ? `${documentPage.parent ? 'Suboperation' : 'Operation'} ${orderNumber}`
    : documentPage.name

  if (isPrintPage && hasRealSubPages) {
    const firstSubPageId = effectiveSubPages[0]

    return (
      <AnnotationsMultiSelectContainer isReadOnly={isReadOnly}>
        {effectiveSubPages.map((subPageId, index) => {
          const printDocumentPage = {
            ...documentPage,
            template_values: {
              ...documentPage.template_values,
              subPages: [subPageId],
              printFirstSubPageId: firstSubPageId,
              printSubPageId: subPageId,
            },
          } as unknown as FlattenedDocumentPage

          return (
            <DocumentTemplate
              parentRef={index === 0 ? parentRef : undefined}
              key={subPageId}
              id={`${documentPage.id}-${subPageId}`}
              title={title}
              createdAt={createdAt}
              versionName={versionName}
              isLatestDocumentVersion={isLatestDocumentVersion}
              order={OPERATION_DOCUMENTS_OFFSET + documentOrder + index / 1000}
              testId={`operation-page-${orderNumber}-container-${index + 1}`}
              fullScreen={fullScreen}
              isReadOnly={isReadOnly}
              headerVisibility={index === 0 ? 'visible' : 'hidden'}
              footerVisibility={
                index === effectiveSubPages.length - 1 ? 'visible' : 'hidden'
              }
            >
              <div
                className="flex flex-col flex-1"
                style={{
                  transform:
                    index === 0
                      ? undefined
                      : `translateY(${PRINT_SUBPAGE_CONTENT_OFFSET_Y}px)`,
                }}
              >
                <AnnotationsContainer
                  documentPage={printDocumentPage}
                  isReadOnly={isReadOnly}
                />
              </div>
            </DocumentTemplate>
          )
        })}
      </AnnotationsMultiSelectContainer>
    )
  }

  return (
    <AnnotationsMultiSelectContainer isReadOnly={isReadOnly}>
      <DocumentTemplate
        parentRef={parentRef}
        id={`${documentPage.id}`}
        title={title}
        onEditTitle={(title) => {
          void updateOperation({
            documentPageId: documentPage.id as string,
            values: {
              name: title,
              template_values: documentPage.template_values,
              parent_id: documentPage.parent as string,
            },
          })
        }}
        createdAt={createdAt}
        versionName={versionName}
        isLatestDocumentVersion={isLatestDocumentVersion}
        order={OPERATION_DOCUMENTS_OFFSET + documentOrder}
        testId={`operation-page-${orderNumber}-container`}
        fullScreen={fullScreen}
        isReadOnly={isReadOnly}
        height={effectiveSubPages.length}
      >
        {/* Comments button — stays at top-right of first subpage */}
        {isInView &&
          !isReadOnly &&
          !hideCommentsButton &&
          (unresolvedComments?.length || 0) > 0 && (
            <div
              className="z-[10] flex flex-col gap-2 absolute top-0 right-0"
              style={{ transform: 'translateX(120%)' }}
            >
              <Button
                variant="ghost"
                className="!h-auto px-2 p-1 pr-2"
                onClick={() => setCommentsEnabled(true)}
              >
                <div className="flex flex-col items-center space-y-2 cursor-pointer relative text-slate-400">
                  <span className="absolute top-[2px] right-[-5px] text-xs">
                    {unresolvedComments?.length}
                  </span>
                  <ChatBubbleOvalLeftEllipsisIcon className="w-6 h-6" />
                </div>
              </Button>
            </div>
          )}

        {/* Per-subpage floating controls (author only) */}
        {isInView &&
          effectiveSubPages.map((subPageId, i) => (
            <SubpageControls
              key={subPageId}
              subPageId={subPageId}
              index={i}
              isFirst={i === 0}
              isLast={i === effectiveSubPages.length - 1}
              isHidden={false}
              isReadOnly={isReadOnly}
              documentPage={documentPage}
              documentVersionId={documentPage.document_version}
              onAddSubpage={() => addSubpage(uuidv4(), subPageId)}
              onRemoveSubpage={removeSubpage}
            />
          ))}
        <AnnotationsContainer
          documentPage={patchedDocumentPage}
          fullSubPageIds={subPages}
          hiddenSubPageIds={hiddenSubPageIds}
          isReadOnly={isReadOnly}
        >
          {shouldRenderEmptyDocumentBanner && (
            <EmptyDocumentBanner
              isReadOnly={isReadOnly}
              pageId={documentPage.id as string}
              projectId={docData.project.id}
              documentId={docData.doc?.id as string}
              onDismiss={onDismissEmptyStateBanner}
            >
              <div className="text-center text-primary-40 w-[450px]">
                <h4 className="mb-2 mt-1 font-medium text-lg">
                  Link this operation to CAD
                </h4>
                <p className="text-sm">
                  Drag part(s) from the Assembly Tree to the Operation step.
                  Your Parts BOM will update to reflect that link.
                </p>
              </div>
            </EmptyDocumentBanner>
          )}
          {shouldRenderNoViewsBanner ? (
            <EmptyDocumentBanner
              isReadOnly={isReadOnly}
              buttonSize={24}
              pageId={documentPage.id as string}
              projectId={docData.project.id}
              documentId={docData.doc?.id as string}
              onDismiss={onDismissEmptyStateBanner}
            >
              <div className="text-center text-primary-40 w-[450px]">
                <h4 className="mb-2 mt-1 font-normal text-md">
                  Add screenshots from CAD view
                </h4>
              </div>
            </EmptyDocumentBanner>
          ) : null}
        </AnnotationsContainer>
      </DocumentTemplate>
    </AnnotationsMultiSelectContainer>
  )
}

type EmptyDocumentBannerProps = {
  isReadOnly?: boolean
  children: React.ReactNode
  buttonSize?: number
  pageId: string
  projectId: string
  documentId: string
  onDismiss?: () => void
}
const EmptyDocumentBanner = ({
  isReadOnly,
  children,
  buttonSize,
  pageId,
  projectId,
  documentId,
  onDismiss,
}: EmptyDocumentBannerProps) => {
  const [, setLocation] = useLocation()
  const buttonWidthClass = useMemo(
    () => (buttonSize ? `w-${buttonSize}` : ''),
    [buttonSize],
  )
  const buttonHeightClass = useMemo(
    () => (buttonSize ? `h-${buttonSize}` : ''),
    [buttonSize],
  )

  const pageLink = useMemo(
    () => `~/p/${projectId}/document/${documentId}/cad?stepId=${pageId}`,
    [projectId, documentId, pageId],
  )

  return !isReadOnly ? (
    <div
      data-disableprint
      style={{
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      }}
      className="absolute flex flex-col items-center"
    >
      <EmptyDocumentButton
        className={`${buttonWidthClass} ${buttonHeightClass}`}
        onClick={(event) => {
          event.stopPropagation()
          event.preventDefault()
          setLocation(pageLink)
        }}
      />
      {children}
      <div className="flex items-center content-center mt-2">
        <Button
          variant="outline"
          className="bg-slate-200 text-slate-500 hover:text-slate-400 rounded-2xl px-2 py-0.5"
          onClick={onDismiss}
        >
          Dismiss
        </Button>
      </div>
    </div>
  ) : null
}

const EmptyDocumentButton = ({
  className,
  onClick,
}: {
  className?: string
  onClick: MouseEventHandler<HTMLButtonElement>
}) => {
  const [isHovering, setIsHovering] = useState(false)

  return (
    <Button
      variant="ghost"
      onClick={onClick}
      className="h-auto hover:bg-transparent"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <EmptyDocumentIcon
        className={className}
        strokeWidth={isHovering ? '2' : '1'}
      />
    </Button>
  )
}
